import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { CircularProgress } from "@material-ui/core";
import { withAlert } from "react-alert";
import _ from "lodash";
import { withTranslation } from "react-i18next";
import DialogTitle from "../../waybee-ui/Dialog/DialogTitle";
import Dialog from "../../waybee-ui/Dialog";
import ClassService from "../../services/ClassService";
import UserStore from "../../utils/UserStore";
import fetchGradeJobQueue from "../../utils/fetchGradeJobQueue";
import GradesList from "./GradesList";

const TitleDetails = styled.p`
  font-size: 16px;
  margin: 0;
  color: rgba(0, 0, 0, 0.5);
`;

const LoadContainer = styled.div`
  width: 100%;
  text-align: center;
  padding: 32px 8px;
`;

class GradesDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      students: [],
      loadingStudents: false,
      loadingSave: false
    };
  }

  componentDidMount() {
    const { selectedExam } = this.props;
    if (selectedExam) this.getGrades();
  }

  componentDidUpdate(prevProps) {
    const { selectedExam } = this.props;

    if (selectedExam && selectedExam !== prevProps.selectedExam)
      this.getGrades();
  }

  getGrades = async () => {
    const { alert, selectedExam, t } = this.props;

    this.setState({ loadingStudents: true });

    try {
      const studentGrades = await ClassService.getGrades(
        selectedExam.idTurmaDisc,
        selectedExam.idStep,
        selectedExam.idExam
      );
      this.setState({ students: studentGrades });
    } catch (e) {
      console.error(e);
      alert.show(t("grades.v1.errorGrades"), {
        title: t("common.actions.error")
      });
    } finally {
      this.setState({ loadingStudents: false });
    }
  };

  getInitialValues = () => {
    const { students } = this.state;
    const initialState = {
      grades: {},
      comments: {}
    };
    students.forEach(student => {
      initialState.grades[student.ra] = student.grade;
      initialState.comments[student.ra] = student.comment;
    });
    return initialState;
  };

  saveGrades = async values => {
    const { students } = this.state;
    const { alert, selectedExam, classId, course, onClose, t } = this.props;

    this.setState({ loadingSave: true });

    const oldGrades = students
      .filter(student => student.grade)
      .map(student => ({
        studentId: student.ra,
        value: student.grade,
        comment: student.comment
      }));

    const formattedValues = Object.keys(values.grades)
      .map(studentId => {
        const gradeTypeValue = !selectedExam.conceptGrades
          ? Number(values.grades[studentId])
          : values.grades[studentId];

        const conceptValue = selectedExam.conceptGrades
          ? selectedExam.conceptGrades.find(
              grade => grade.CODCONCEITO === gradeTypeValue
            )
          : null;

        return {
          studentId,
          value: _.isNil(values.grades[studentId]) ? undefined : gradeTypeValue,
          comment: values.comments[studentId] || undefined,
          conceptValue: conceptValue ? conceptValue.NOTA : null
        };
      })
      .filter(student => !_.isNil(student.value));

    const deletedGrades = _.differenceWith(
      oldGrades,
      formattedValues,
      (a, b) => a.studentId === b.studentId
    ).map(grades => ({
      ...grades,
      value: "",
      comment: ""
    }));

    const newValues = _.differenceWith(formattedValues, oldGrades, _.isEqual);
    const grades = newValues.concat(deletedGrades);

    if (!grades.length) {
      this.setState({ loadingSave: false });
      alert.show(t("grades.v1.noChanges"), {
        title: t("grades.v1.noChangesTitle")
      });
      return;
    }

    const period =
      selectedExam.name || `${selectedExam.idStep}-${selectedExam.idExam}`;
    const courseId =
      (course && (course.id != null ? course.id : course.idTurmaDisc)) ||
      selectedExam.idTurmaDisc;

    const data = {
      classId,
      filter: {
        course: { id: courseId },
        period
      },
      externalData: UserStore.getUserExternalInfo(),
      idExam: selectedExam.idExam,
      idStep: selectedExam.idStep,
      typeStep: selectedExam.typeStep,
      idTurmaDisc: selectedExam.idTurmaDisc,
      idGroup: selectedExam.idGroup,
      grades: !selectedExam.conceptGrades ? grades : undefined,
      conceptGrades: selectedExam.conceptGrades ? grades : undefined
    };

    try {
      await ClassService.saveGradesJob(data);
      alert.show(t("common.messages.processingRequest"), {
        title: t("grades.v1.gradesSent")
      });
      fetchGradeJobQueue();
      if (onClose) {
        onClose();
      }
    } catch (e) {
      console.error(e);
      const errMsg =
        e && typeof e.message === "string" ? e.message : String(e || "");
      if (!errMsg.includes("timeout")) {
        alert.show(t("grades.v2.saveError", { err: errMsg }), {
          title: t("common.actions.error")
        });
      }
    } finally {
      this.setState({ loadingSave: false });
    }
  };

  render() {
    const { onClose, selectedExam, t } = this.props;
    const { students, loadingStudents, loadingSave } = this.state;

    if (!selectedExam) return null;
    return (
      <>
        <Dialog open onClose={onClose} maxWidth="md" fullWidth closeButton>
          <DialogTitle>
            {selectedExam.name}
            {!selectedExam.conceptGrades ? (
              <TitleDetails>
                {t("grades.v1.valueAverage", {
                  value: selectedExam.value,
                  average: selectedExam.average
                })}
              </TitleDetails>
            ) : (
              ""
            )}
          </DialogTitle>

          {loadingStudents ? (
            <LoadContainer>
              <CircularProgress />
            </LoadContainer>
          ) : (
            <GradesList
              students={students}
              onSubmit={this.saveGrades}
              maxValue={selectedExam.value}
              concept={selectedExam.conceptGrades}
              average={selectedExam.average}
              defaultValues={this.getInitialValues()}
            />
          )}
        </Dialog>
        <Dialog
          onClose={() => this.setState({ loadingSave: false })}
          open={loadingSave}
        >
          <DialogTitle>{t("grades.v1.sending")}</DialogTitle>
          <LoadContainer>
            <CircularProgress />
          </LoadContainer>
        </Dialog>
      </>
    );
  }
}

GradesDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  classId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  course: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    idTurmaDisc: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  selectedExam: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number,
    average: PropTypes.number,
    idTurmaDisc: PropTypes.number,
    idGroup: PropTypes.number,
    idStep: PropTypes.oneOf([PropTypes.string, PropTypes.number]),
    idExam: PropTypes.oneOf([PropTypes.string, PropTypes.number]),
    typeStep: PropTypes.string,
    conceptGrades: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired,
  t: PropTypes.func.isRequired
};

GradesDialog.defaultProps = {
  classId: null,
  course: null
};

export default withTranslation()(withAlert()(GradesDialog));
